// LibraryView.jsx
// Unified workspace for post-shift curation. Renders either the Pancakes lens
// (GalleryView) or the Shifts lens (ShiftArchiveView) inside a persistent tab
// strip. Existing /gallery and /archive URLs redirect into this view preserving
// bookmarks and in-code navigations.
//
// Tab is driven by the ?tab= query param (pancakes | shifts, default pancakes).
// Each child view gets an `embedded` prop that suppresses its own ← Menu button;
// the tab bar owns that affordance for the whole workspace.

import { useState, useEffect } from "react";
import GalleryView from "../GalleryView";
import ShiftArchiveView from "../ShiftArchiveView";
import PracticeReviewTab from "./PracticeReviewTab";
import { STAFF_THEME, FONT } from "../theme/staff";

const BRAND = STAFF_THEME;

function readTabFromUrl() {
  const sp = new URLSearchParams(window.location.search);
  const t  = sp.get("tab");
  return t === "shifts" || t === "practice" ? t : "pancakes";
}

export default function LibraryView({ user, navigate }) {
  const [tab, setTab] = useState(readTabFromUrl);

  // Keep local state in sync with back/forward navigation and external pushes.
  useEffect(() => {
    const onPop = () => setTab(readTabFromUrl());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  function switchTab(next) {
    if (next === tab) return;
    setTab(next);
    const url = `/library?tab=${next}`;
    window.history.pushState(null, "", url);
  }

  return (
    <div style={{ minHeight: "100vh", background: BRAND.dark, display: "flex", flexDirection: "column", fontFamily: FONT.body }}>

      {/* Workspace tab strip — sits above each child view's own header. */}
      <div style={{
        display: "flex", alignItems: "center",
        background: BRAND.panel,
        borderBottom: `1px solid ${BRAND.border}`,
        padding: "0.4rem 0.75rem",
        gap: "0.4rem",
        flexShrink: 0,
        flexWrap: "wrap",
      }}>
        <div style={{
          color: "rgba(255,218,173,0.55)",
          fontFamily: FONT.display,
          fontSize: "0.68rem",
          fontWeight: 600,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          marginRight: "0.5rem",
          flexShrink: 0,
        }}>
          Library
        </div>
        <TabButton active={tab === "pancakes"} onClick={() => switchTab("pancakes")} label="🖼 Pancakes" />
        <TabButton active={tab === "shifts"}   onClick={() => switchTab("shifts")}   label="📋 Shifts" />
        <TabButton active={tab === "practice"} onClick={() => switchTab("practice")} label="🎓 Practice" />
        <div style={{ flex: 1 }} />
        <button
          onClick={() => navigate("/menu")}
          style={{
            background: "transparent",
            border: `1px solid ${BRAND.border}`,
            color: BRAND.muted,
            borderRadius: "8px",
            padding: "0.35rem 0.7rem",
            fontSize: "0.78rem",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          ← Menu
        </button>
      </div>

      {/* Child view — keeps its own chrome below the tab strip, minus the ← Menu */}
      <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        {tab === "pancakes" && <GalleryView      navigate={navigate} embedded />}
        {tab === "shifts"   && <ShiftArchiveView user={user} navigate={navigate} embedded />}
        {tab === "practice" && <PracticeReviewTab />}
      </div>
    </div>
  );
}

function TabButton({ active, onClick, label }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: active ? BRAND.gold : "transparent",
        color: active ? BRAND.dark : BRAND.muted,
        border: active ? `1px solid ${BRAND.gold}` : `1px solid ${BRAND.border}`,
        borderRadius: "8px",
        padding: "0.35rem 0.75rem",
        fontSize: "0.82rem",
        fontWeight: active ? 800 : 600,
        cursor: "pointer",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </button>
  );
}
